import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const CompaniesApi = createApi({
	reducerPath: "Companies",
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.REACT_APP_BACKEND_URL}`,
		credentials: "include",
	}),
	endpoints: (builder) => ({
		getAllCompanies: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const order = val.order ? `&order=${val.order}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `/companies?page=${page}&limit=${limit}${search}${sortBy}${order}${queryString}`;
			},
		}),
		getSpecificCompany: builder.query({
			query: (id) => `/companies/${id}`,
		}),
		deleteCompany: builder.mutation({
			query: (id) => ({
				url: `/companies/${id}`,
				method: "DELETE",
			}),
		}),
		deleteCompanyAttachments: builder.mutation({
			query: (id) => ({
				url: `/companies/attachments/${id}`,
				method: "DELETE",
			}),
		}),
		createCompany: builder.mutation({
			query: (payload) => {
				const formData = new FormData();

				// Append all fields to FormData
				Object.keys(payload).forEach((key) => {
					if (key === "attachments" && Array.isArray(payload[key])) {
						// Handle multiple files
						payload[key].forEach((file) => {
							formData.append("attachments", file);
						});
					} else if (key === "trade" && Array.isArray(payload[key])) {
						// Handle trade array - send as multiple trade parameters or comma-separated
						payload[key].forEach((trade) => {
							formData.append("trade", trade);
						});
					} else if (
						payload[key] !== null &&
						payload[key] !== undefined &&
						payload[key] !== ""
					) {
						formData.append(key, payload[key]);
					}
				});

				return {
					url: "/companies",
					method: "POST",
					body: formData,
				};
			},
		}),
		editCompany: builder.mutation({
			query: ({ id, payload }) => {
				const formData = new FormData();

				// Append all fields to FormData
				Object.keys(payload).forEach((key) => {
					if (key === "attachments" && Array.isArray(payload[key])) {
						// Handle multiple files
						payload[key].forEach((file) => {
							formData.append("attachments", file);
						});
					} else if (key === "trade" && Array.isArray(payload[key])) {
						// Handle trade array
						payload[key].forEach((trade) => {
							formData.append("trade", trade);
						});
					} else if (
						payload[key] !== null &&
						payload[key] !== undefined &&
						payload[key] !== ""
					) {
						formData.append(key, payload[key]);
					}
				});

				return {
					url: `/companies/${id}`,
					method: "PUT",
					body: formData,
				};
			},
		}),
		assignProjects: builder.mutation({
			query: ({ companyId, payload }) => ({
				url: `/companies/${companyId}/projects`,
				method: "POST",
				body: payload,
			}),
		}),
		getCompanyProjects: builder.query({
			query: (companyId) => `/companies/${companyId}/projects`,
		}),
		getCompanyProjectTrades: builder.query({
			query: (companyProjectId) =>
				`/companies/company-projects/${companyProjectId}/trades`,
		}),
		getCompanyTrades: builder.query({
			query: () => `/companies/trades`,
		}),
		deleteCompanyProject: builder.mutation({
			query: ({ companyId, projectId }) => ({
				url: `/companies/${companyId}/projects/${projectId}`,
				method: "DELETE",
			}),
		}),
		editCompanyAssignedProjects: builder.mutation({
			query: ({ companyId, projectId, payload }) => ({
				url: `/companies/${companyId}/projects/${projectId}`,
				method: "PUT",
				body: payload,
			}),
		}),
	}),
});

export const {
	useGetAllCompaniesQuery,
	useDeleteCompanyMutation,
	useGetSpecificCompanyQuery,
	useCreateCompanyMutation,
	useEditCompanyMutation,
	useDeleteCompanyAttachmentsMutation,
	useAssignProjectsMutation,
	useGetCompanyProjectsQuery,
	useLazyGetCompanyProjectsQuery,
	useGetCompanyProjectTradesQuery,
	useLazyGetCompanyProjectTradesQuery,
	useGetCompanyTradesQuery,
	useDeleteCompanyProjectMutation,
	useEditCompanyAssignedProjectsMutation,
} = CompaniesApi;
